import { router } from 'expo-router';
import React, { useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { WebView } from 'react-native-webview';

import { Heading } from '@squire/ui';

import { CloseButton } from '@/components/_shared/CloseButton';
import { Page } from '@/components/_shared/Page';
import { getEnvironmentConfig } from '@/constants/config';
import { routes } from '@/constants/routes';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { ReferralWebViewScreenProps } from './types';

export const buildReferralUrl = (shopId: string, customerId: string) =>
  `${getEnvironmentConfig().webUrl}referrals/shop/${shopId}/customer/${customerId}`;

const ReferralWebViewScreen: React.FC<ReferralWebViewScreenProps> = ({
  shopId,
  customerId,
}) => {
  const { t } = useScreenTranslations('ReferralWebViewScreen');
  const { t: tShared } = useScreenTranslations('shared');
  const url = buildReferralUrl(shopId, customerId);
  const [isLoading, setIsLoading] = useState(true);

  const handleClose = () => {
    router.canGoBack() ? router.back() : router.replace(routes.home);
  };

  const handleLoaded = () => setIsLoading(false);

  return (
    <Page
      edges={['top', 'left', 'right']}
      backgroundColor="backgroundPrimaryInverse"
    >
      <View style={styles.header}>
        <View style={styles.titleContainer}>
          <Heading variant="m" color="textPrimaryInverse">
            {t('title')}
          </Heading>
        </View>
        <CloseButton
          inverse
          onPress={handleClose}
          accessibilityLabel={tShared(
            'screenHeader.closeButton.accessibilityLabel',
          )}
          accessibilityHint={tShared(
            'screenHeader.closeButton.accessibilityHint',
          )}
        />
      </View>
      <View style={styles.container}>
        <WebView
          source={{ uri: url }}
          style={styles.webView}
          onLoadEnd={handleLoaded}
        />
        {isLoading ? (
          <View style={styles.loadingOverlay} pointerEvents="none">
            <ActivityIndicator
              size="large"
              accessibilityLabel={t('loadingLabel')}
            />
          </View>
        ) : null}
      </View>
    </Page>
  );
};

ReferralWebViewScreen.displayName = 'ReferralWebViewScreen';
export { ReferralWebViewScreen };
